import { Paper, Table, TableBody, TableContainer } from '@material-ui/core';
import styles from '../Components.module.scss';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import { ASSET_DETAILED_PATH, LOCATION_DETAILED_PATH } from 'constants/routeConstants';
import React, { useMemo } from 'react';
import LinkComponent from 'components/LinkComponent/LinkComponent';
import { formatDate, formatPrice } from 'helpers/AppHelpers';
import clsx from 'clsx';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { tableHeadConfig } from '../../tableConfigs';
import { TableHeadComponent } from 'components/SummaryComponents';
import InfoTooltip from 'components/InfoTooltip/InfoTooltip';
import { useUserConfig } from 'hooks/useUserConfig';
import { getExpirationStatus, getRowExpiration } from '../../Summary/helpers';

export default function LooseItemsTable({
  tableBodyData,
  isUnitsCostsColumnAvailable,
  isNextVerificationColumnAvailable,
  totalQuantity,
  totalUnitsCosts,
  selectionState,
  isStatusVerifiable
}) {
  const { isBasicRoleUser, isPersonnelManagerUser } = useUserConfig();
  const isSelectionAvailable = !isBasicRoleUser && !isPersonnelManagerUser;

  const { isAllSelected, toggleAllSelection, isRowSelected, toggleRowSelection } = selectionState;

  const canDisplayCell = (rowId, rowIndex) =>
    !rowId ||
    rowIndex === 0 ||
    !(tableBodyData[rowIndex]?.location?.id === tableBodyData[rowIndex - 1]?.location?.id);

  const getColumnSpan = (rowLocationId) =>
    tableBodyData.filter(({ location }) => location.id === rowLocationId).length;

  const createTableConfig = (isAllSelected, toggleAllSelection) => {
    const config = [...tableHeadConfig];

    if (isSelectionAvailable) {
      config.unshift({
        label: '',
        renderContent: () => (
          <CustomCheckbox
            sendEvent
            isDisabled={tableBodyData.length === 0}
            value={isAllSelected()}
            onChange={(e) => toggleAllSelection(e.target.checked)}
          />
        )
      });
    }

    const columnsToInsert = [
      {
        condition: isNextVerificationColumnAvailable,
        afterLabel: 'Contained In',
        column: { id: 11, label: 'Next Verification' }
      },
      {
        condition: isUnitsCostsColumnAvailable,
        afterLabel: 'Current QTY',
        column: { id: 12, label: 'Units Costs' }
      }
    ];

    columnsToInsert.forEach(({ condition, afterLabel, column }) => {
      if (condition) {
        const insertIndex = config.findIndex((col) => col.label === afterLabel) + 1;
        if (insertIndex > 0) {
          config.splice(insertIndex, 0, column);
        }
      }
    });

    return config;
  };

  const tableConfig = useMemo(
    () => createTableConfig(isAllSelected, toggleAllSelection),
    [isAllSelected, toggleAllSelection, isSelectionAvailable]
  );

  const getTotalsColSpan = () => {
    let totalColumns = tableHeadConfig.length;

    if (isSelectionAvailable) {
      totalColumns += 1;
    }
    if (isNextVerificationColumnAvailable) {
      totalColumns += 1;
    }
    if (isUnitsCostsColumnAvailable) {
      totalColumns += 1;
    }

    let span = totalColumns - 1;

    if (isUnitsCostsColumnAvailable) {
      span -= 1;
    }

    return span;
  };

  return (
    <TableContainer className={styles.tableContainer} component={Paper}>
      <Table>
        <TableHeadComponent config={tableConfig} />
        <TableBody>
          {tableBodyData?.length ? (
            <>
              {tableBodyData.map((rowData, rowIndex) => (
                <StyledTableRow key={rowData.id}>
                  {isSelectionAvailable && (
                    <StyledTableCell className={styles.checkboxCell}>
                      {isStatusVerifiable(rowData) && (
                        <CustomCheckbox
                          sendEvent
                          value={isRowSelected(rowIndex)}
                          onChange={(e) => toggleRowSelection(rowIndex, e.target.checked)}
                        />
                      )}
                    </StyledTableCell>
                  )}
                  <StyledTableCell className={styles.locationCell}>
                    <LinkComponent
                      path={`${LOCATION_DETAILED_PATH}/${rowData.location?.id}`}
                      name={rowData.location?.siteCode}
                      isRedirectAvailable={!rowData.location?.deleted}
                    />
                  </StyledTableCell>
                  {(() => {
                    const exp = getRowExpiration(rowData);
                    const st = getExpirationStatus(exp);
                    return (
                      <StyledTableCell className={clsx(styles.expiration, st && styles[st])}>
                        {exp || ''}
                      </StyledTableCell>
                    );
                  })()}
                  <StyledTableCell className={styles.sublocationCell}>
                    {rowData?.sublocation?.name || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.containedInCell}>
                    <LinkComponent
                      path={`${ASSET_DETAILED_PATH}/${rowData.containedInAsset?.id}`}
                      name={rowData.containedInAsset?.drCode}
                    />
                  </StyledTableCell>
                  {isNextVerificationColumnAvailable && (
                    <StyledTableCell className={styles.nextVerificationDateCell}>
                      {rowData.nextVerificationDate ? formatDate(rowData.nextVerificationDate) : ''}
                    </StyledTableCell>
                  )}
                  <StyledTableCell className={styles.noteCell}>
                    {rowData?.notes || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.statusCell}>
                    {rowData?.status?.displayName || ''}
                    {rowData?.status?.displayName === 'In Picklist'
                      ? ` (${rowData?.picklist?.name})`
                      : ''}
                    {rowData?.status?.displayName === 'In Leavelist'
                      ? ` (${rowData?.leavelist?.name})`
                      : ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.minQtyCell}>
                    {rowData?.minimumQuantity || 0}
                  </StyledTableCell>
                  <StyledTableCell className={styles.byAheadAccountCell}>
                    {rowData?.byAheadAccount || 0}
                  </StyledTableCell>
                  <StyledTableCell
                    className={clsx(
                      styles.currentQtyCell,
                      rowData?.needsVerification && styles.warning
                    )}>
                    <div className={styles.qtyContent}>
                      {rowData.quantity || 0}
                      {rowData?.needsVerification && <InfoTooltip text="Needs to be verified" />}
                    </div>
                  </StyledTableCell>
                  {isUnitsCostsColumnAvailable && (
                    <StyledTableCell className={styles.unitsCostsCell}>
                      {formatPrice(rowData.unitCosts)}
                    </StyledTableCell>
                  )}
                  {canDisplayCell(rowData.id, rowIndex) && (
                    <StyledTableCell
                      rowSpan={rowData?.location?.id && getColumnSpan(rowData.location.id)}
                      className={clsx(styles.locationQtyCell, styles.merged)}>
                      {rowData.locationQuantity || 0}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))}
              <StyledTableRow>
                <StyledTableCell
                  colSpan={getTotalsColSpan()}
                  style={{ textAlign: 'right', fontWeight: 'bold' }}>
                  Totals:
                </StyledTableCell>
                {isUnitsCostsColumnAvailable && (
                  <StyledTableCell className={styles.unitsCostsCell} style={{ fontWeight: 'bold' }}>
                    {formatPrice(totalUnitsCosts)}
                  </StyledTableCell>
                )}
                <StyledTableCell className={styles.locationQtyCell} style={{ fontWeight: 'bold' }}>
                  {totalQuantity || 0}
                </StyledTableCell>
              </StyledTableRow>
            </>
          ) : (
            <StyledTableRow>
              <StyledTableCell colSpan={10} className={styles.emptyTable}>
                No available data in the table.
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
